import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, QrCode, MapPin, Umbrella, Tag, Plus, X, Check, Loader2, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/common/TopBar';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { orderService, paymentService, cardVaultService } from '../services/api';
import api from '../services/api';
import { initPagBankSDK, createCardToken, isPagBankReady, getPagBankConfig } from '../services/pagbankSDK';
import { toast } from 'sonner';

const Checkout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cart, kiosk, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { location, requestLocation } = useLocation();

  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [reference, setReference] = useState('');
  const [umbrellaColor, setUmbrellaColor] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedCard, setSelectedSavedCard] = useState(null);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [pagbankReady, setPagbankReady] = useState(false);
  const [cardTokenizing, setCardTokenizing] = useState(false);
  
  // Card form state for PagBank SDK
  const [cardForm, setCardForm] = useState({
    number: '',
    cvv: '',
    expiryMonth: '',
    expiryYear: '',
    holderName: '',
    holderCPF: '',
    holderBirthDate: '',
    holderPhone: '',
    nickname: '',
  });

  const pagbankInitRef = useRef(false);

  // Initialize PagBank SDK on mount
  useEffect(() => {
    if (pagbankInitRef.current) return;
    pagbankInitRef.current = true;

    const config = getPagBankConfig();
    const publicKey = config.publicKey;
    const environment = config.environment;

    if (publicKey) {
      const initialized = initPagBankSDK({ publicKey, environment });
      if (initialized) {
        setPagbankReady(true);
      }
    }
  }, []);

  // Load saved cards only after authentication is available.
  useEffect(() => {
    if (!user?.id) return;
    loadSavedCards();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadSavedCards = async () => {
    try {
      const cards = await cardVaultService.list();
      setSavedCards(cards);
      // Auto-select default card if exists
      const defaultCard = cards.find(c => c.is_default);
      if (defaultCard && !selectedSavedCard) {
        setSelectedSavedCard(defaultCard);
      }
    } catch (e) {
      console.error('Failed to load saved cards:', e);
    }
  };

  // Handle saving a new card via PagBank JS SDK
  const handleTokenizeAndSaveCard = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!cardForm.number || !cardForm.cvv || !cardForm.expiryMonth || !cardForm.expiryYear || 
        !cardForm.holderName || !cardForm.holderCPF) {
      toast.error('Preencha todos os campos do cartão');
      return;
    }

    setCardTokenizing(true);
    setSavingCard(true);

    try {
      // Step 1: Tokenize card via PagBank SDK (PAN never touches our backend)
      const pagbankCardToken = await createCardToken({
        number: cardForm.number.replace(/\s/g, ''),
        cvv: cardForm.cvv,
        expiryMonth: cardForm.expiryMonth.padStart(2, '0'),
        expiryYear: '20' + cardForm.expiryYear,
        holderName: cardForm.holderName,
        holderCPF: cardForm.holderCPF.replace(/\D/g, ''),
      });

      // Step 2: Detect brand (optional, can also be done in SDK)
      let brand = 'visa';
      const firstDigits = cardForm.number.replace(/\s/g, '').slice(0, 6);
      if (firstDigits.startsWith('5') || firstDigits.startsWith('2')) brand = 'mastercard';
      else if (firstDigits.startsWith('4')) brand = 'visa';
      else if (firstDigits.startsWith('3')) brand = 'amex';
      else if (firstDigits.startsWith('6')) brand = 'elo';
      else if (firstDigits.startsWith('606282') || firstDigits.startsWith('637095')) brand = 'hipercard';

      // Step 3: Send token + metadata to backend
      const cardData = {
        pagbank_card_token: pagbankCardToken,
        brand,
        last_four: cardForm.number.replace(/\s/g, '').slice(-4),
        expiry_month: parseInt(cardForm.expiryMonth),
        expiry_year: parseInt('20' + cardForm.expiryYear),
        holder_name: cardForm.holderName,
        nickname: cardForm.nickname || undefined,
      };

      await api.post('/payments/pagbank/cards/tokenize', cardData);
      toast.success('Cartão salvo com sucesso!');

      // Reset form and close modal
      setShowAddCardModal(false);
      setCardForm({
        number: '', cvv: '', expiryMonth: '', expiryYear: '',
        holderName: '', holderCPF: '', holderBirthDate: '', holderPhone: '', nickname: '',
      });

      // Reload saved cards
      const response = await api.get('/payments/pagbank/cards');
      setSavedCards(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Erro ao salvar cartão');
    } finally {
      setCardTokenizing(false);
      setSavingCard(false);
    }
  };

  const subtotal = getTotal();
  const discount = appliedCoupon?.discount_amount || 0;
  const total = Math.max(0, subtotal - discount);

  // Guard against empty cart
  useEffect(() => {
    if (cart.length === 0 && !loading && !orderConfirmed) {
      navigate('/cart', { replace: true });
    }
  }, [cart.length, loading, orderConfirmed, navigate]);

  if ((cart.length === 0 && !orderConfirmed) || !kiosk) {
    return null;
  }

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post(`/coupons/validate/${couponCode.toUpperCase()}`, null, {
        params: { order_value: subtotal },
      });
      setAppliedCoupon(data);
      toast.success(`Cupom aplicado! -R$ ${data.discount_amount.toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cupom inválido');
      setAppliedCoupon(null);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleConfirmOrder = async () => {
    if (!reference.trim()) {
      toast.error('Informe um ponto de referência para a entrega');
      return;
    }

    setLoading(true);

    try {
      const coords = location
        ? [location.longitude, location.latitude]
        : (kiosk?.location?.coordinates ?? [0, 0]);

      const orderData = {
        client_id: kiosk.id,
        customer_id: user.id,
        items: cart,
        subtotal: subtotal,
        delivery_fee: 0.0,
        total: total,
        coupon_code: appliedCoupon ? couponCode.toUpperCase() : null,
        discount_amount: discount,
        delivery_location: {
          location: {
            type: 'Point',
            coordinates: coords,
          },
          reference: reference,
          umbrella_color: umbrellaColor,
          other_reference: notes,
        },
        notes: notes,
      };

      const order = await orderService.create(orderData);
      toast.success('Pedido criado!');

      // Create payment with saved card if selected
      const savedCardToken = selectedSavedCard && paymentMethod === 'credit_card'
        ? selectedSavedCard.card_token
        : null;
      const payment = await paymentService.createPayment(order.id, paymentMethod, savedCardToken);
      toast.success('Pagamento iniciado!');

      setOrderConfirmed(true);
      navigate(`/order/${order.id}`);
      clearCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pedido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="checkout-page">
      <TopBar title={t('checkout.title')} showBack />

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Order Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3">{t('checkout.summary')}</h2>
          <div className="space-y-2 mb-3">
            {cart.map((item) => (
              <div key={item.product_id} className="flex justify-between text-sm">
                <span>{item.quantity}x {item.product_name}</span>
                <span className="font-semibold">R$ {item.subtotal.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-2 space-y-1">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{t('cart.subtotal')}</span>
              <span>R$ {subtotal.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-secondary">
                <span>{t('cart.discount')} ({couponCode})</span>
                <span>-R$ {discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base pt-1 border-t border-border">
              <span>{t('cart.total')}</span>
              <span className="text-primary">R$ {total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Coupon */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3 flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            {t('checkout.couponCode')}
          </h2>
          {appliedCoupon ? (
            <div className="flex items-center justify-between bg-secondary/10 border border-secondary/20 rounded-xl p-3">
              <div>
                <p className="font-bold text-secondary">{couponCode}</p>
                <p className="text-xs text-muted-foreground">{appliedCoupon.description}</p>
                <p className="text-sm font-bold mt-1">-R$ {appliedCoupon.discount_amount.toFixed(2)}</p>
              </div>
              <button
                data-testid="remove-coupon"
                onClick={removeCoupon}
                className="text-sm text-destructive font-semibold"
              >
                {t('common.delete')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                data-testid="checkout-coupon-input"
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder={t('checkout.couponCode')}
                className="flex-1 px-3 py-2 border border-border rounded-full text-sm uppercase focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                data-testid="apply-coupon"
                onClick={applyCoupon}
                disabled={!couponCode.trim()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-semibold disabled:opacity-50 active:scale-95"
              >
                {t('checkout.applyCoupon')}
              </button>
            </div>
          )}
        </div>

        {/* Delivery Info */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-secondary" />
            {t('checkout.where')}
          </h2>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('checkout.reference')} *
              </label>
              <input
                data-testid="reference-input"
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('checkout.referencePlaceholder')}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <Umbrella className="w-4 h-4 inline mr-1" />
                {t('checkout.umbrellaColor')}
              </label>
              <input
                data-testid="umbrella-color-input"
                type="text"
                value={umbrellaColor}
                onChange={(e) => setUmbrellaColor(e.target.value)}
                placeholder={t('checkout.umbrellaPlaceholder')}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('checkout.notes')}
              </label>
              <textarea
                data-testid="notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('checkout.notesPlaceholder')}
                rows={2}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">
                  {location
                    ? t('checkout.locationActive')
                    : t('checkout.gpsHelp')}
                </p>
              </div>
              {!location && (
                <button
                  onClick={requestLocation}
                  type="button"
                  className="text-xs text-secondary font-semibold underline underline-offset-2 flex items-center gap-1"
                >
                  <MapPin className="w-3 h-3" />
                  {t('checkout.enableLocation')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3">{t('checkout.howToPay')}</h2>
          <div className="space-y-2">
            {/* Saved Cards Section */}
            {savedCards.length > 0 && (
              <div className="space-y-2 mb-3 p-3 bg-primary/5 rounded-xl border border-primary/20">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {t('checkout.savedCards') || 'Cartões Salvos (1-Click)'}
                </h3>
                <div className="space-y-1">
                  {savedCards.map((card) => (
                    <button
                      key={card.card_token}
                      onClick={() => {
                        setPaymentMethod('credit_card');
                        setSelectedSavedCard(card);
                      }}
                      className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                        selectedSavedCard?.card_token === card.card_token
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <div className="text-left flex-1">
                        <p className="font-semibold">
                          {card.brand?.toUpperCase()} •••• {card.last_four}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {card.nickname || 'Cartão salvo'} • {t('checkout.expires') || 'Expira'} {card.expiry_month.toString().padStart(2, '0')}/{card.expiry_year.toString().slice(-2)}
                          {card.is_default && ' • <span className="text-primary text-xs">Padrão</span>'}
                        </p>
                      </div>
                      {selectedSavedCard?.card_token === card.card_token && (
                        <div className="w-5 h-5 rounded-full border-2 border-primary bg-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              data-testid="pay-pix"
              onClick={() => {
                setPaymentMethod('pix');
                setSelectedSavedCard(null);
              }}
              className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                paymentMethod === 'pix' && !selectedSavedCard
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <QrCode className="w-6 h-6 text-primary" />
              <div className="text-left flex-1">
                <p className="font-semibold">{t('checkout.pix')}</p>
                <p className="text-xs text-muted-foreground">{t('checkout.pixSub')}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                paymentMethod === 'pix' && !selectedSavedCard ? 'border-primary bg-primary' : 'border-border'
              }`} />
            </button>

            <button
              data-testid="pay-credit"
              onClick={() => {
                setPaymentMethod('credit_card');
                if (!selectedSavedCard) setSelectedSavedCard(null);
              }}
              className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                paymentMethod === 'credit_card' && !selectedSavedCard
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <CreditCard className="w-6 h-6 text-primary" />
              <div className="text-left flex-1">
                <p className="font-semibold">{t('checkout.creditCard')}</p>
                <p className="text-xs text-muted-foreground">{t('checkout.creditCardSub')}</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                paymentMethod === 'credit_card' && !selectedSavedCard ? 'border-primary bg-primary' : 'border-border'
              }`} />
            </button>

            {/* Google Pay button */}
            <button
              data-testid="pay-google-pay"
              onClick={() => {
                setPaymentMethod('google_pay');
                setSelectedSavedCard(null);
              }}
              className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                paymentMethod === 'google_pay' && !selectedSavedCard
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <Smartphone className="w-6 h-6 text-primary" />
              <div className="text-left flex-1">
                <p className="font-semibold">Google Pay</p>
                <p className="text-xs text-muted-foreground">Pague com Google Pay salvo</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                paymentMethod === 'google_pay' && !selectedSavedCard ? 'border-primary bg-primary' : 'border-border'
              }`} />
            </button>

            {/* Add new card button */}
            {paymentMethod === 'credit_card' && !selectedSavedCard && (
              <button
                onClick={() => setShowAddCardModal(true)}
                disabled={!pagbankReady || cardTokenizing}
                className="w-full p-3 rounded-xl border-2 border-dashed border-secondary flex items-center justify-center gap-2 text-secondary disabled:opacity-50"
              >
                <CreditCard className="w-5 h-5" />
                <span className="font-medium">
                  {t('checkout.addNewCard') || 'Adicionar novo cartão (PagBank SDK)'}
                </span>
                {!pagbankReady && <span className="text-xs text-muted-foreground">(Carregando SDK...)</span>}
              </button>
            )}
          </div>
        </div>

      </div>  {/* End max-w-md mx-auto p-4 space-y-4 */}

      {/* Fixed bottom order button - outside the scrollable area */}
      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto p-4 bg-white border-t border-border z-40">
        <button
          data-testid="confirm-order"
          onClick={handleConfirmOrder}
          disabled={loading}
          className="w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity active:scale-95 shadow-lg disabled:opacity-50"
        >
          {loading ? t('checkout.processing') : `${t('checkout.confirmOrder')} • R$ ${total.toFixed(2)}`}
        </button>
      </div>

      {/* Add Card Modal - PagBank JS SDK Integration */}
      {showAddCardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAddCardModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Outfit'] text-xl font-bold">Adicionar Cartão</h2>
              <button onClick={() => setShowAddCardModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Insira os dados do cartão. O número do cartão e CVV são coletados de forma segura via PagBank SDK (iframe). O PAN nunca toca nosso backend.
            </p>
            <form onSubmit={handleTokenizeAndSaveCard} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Número do Cartão</label>
                <input
                  type="text"
                  value={cardForm.number}
                  onChange={(e) => setCardForm({...cardForm, number: e.target.value})}
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  autoComplete="cc-number"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Validade (MM/AA)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={cardForm.expiryMonth}
                      onChange={(e) => setCardForm({...cardForm, expiryMonth: e.target.value})}
                      placeholder="MM"
                      maxLength={2}
                      className="w-1/2 px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                    <input
                      type="text"
                      value={cardForm.expiryYear}
                      onChange={(e) => setCardForm({...cardForm, expiryYear: e.target.value})}
                      placeholder="AA"
                      maxLength={2}
                      className="w-1/2 px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">CVV</label>
                  <input
                    type="text"
                    value={cardForm.cvv}
                    onChange={(e) => setCardForm({...cardForm, cvv: e.target.value})}
                    placeholder="123"
                    maxLength={4}
                    className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    autoComplete="cc-csc"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nome no Cartão</label>
                <input
                  type="text"
                  value={cardForm.holderName}
                  onChange={(e) => setCardForm({...cardForm, holderName: e.target.value})}
                  placeholder="JOÃO SILVA"
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  autoComplete="cc-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CPF do Titular</label>
                <input
                  type="text"
                  value={cardForm.holderCPF}
                  onChange={(e) => setCardForm({...cardForm, holderCPF: e.target.value})}
                  placeholder="000.000.000-00"
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Apelido (opcional)</label>
                <input
                  type="text"
                  value={cardForm.nickname}
                  onChange={(e) => setCardForm({...cardForm, nickname: e.target.value})}
                  placeholder="Cartão Principal"
                  className="w-full px-3 py-2 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddCardModal(false)}
                  className="flex-1 py-2 border border-border rounded-xl font-semibold text-secondary hover:bg-muted"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingCard || cardTokenizing}
                  className="flex-1 py-2 bg-primary text-primary-foreground rounded-xl font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  {cardTokenizing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2 inline" />
                      Tokenizando...
                    </>
                  ) : savingCard ? (
                    'Salvando...'
                  ) : (
                    'Salvar Cartão'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}  {/* End showAddCardModal */}

    </div>
  );
};

export default Checkout;